import React, { useCallback, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import debounce from 'lodash/debounce'

import { apiManager } from 'helpers'
import { valueOrEmptyString } from 'helpers/strings'
import { AnimatedCheckIcon, Spinner } from 'components'

const MIN_USERNAME_LENGTH = 2

const STATES = {
  INIT: 0,
  VALID: 1,
  INVALID: 2,
  VALIDATING: 3,
  ERROR: 4,
}

const is = (currentState, state) => currentState === state

const UsernameInput = ({ Input, onChange, defaultValue, name, ...rest }) => {
  const currentRequestID = useRef(0)
  const [username, setUsername] = useState(defaultValue)
  const [state, setState] = useState(STATES.INIT)

  const resetInput = useCallback(() => onChange({ target: { value: '', name } }), [onChange, name])

  const validateUsername = useMemo(
    () =>
      debounce(async (e, username, requestID) => {
        resetInput()

        const { exists } = await apiManager.checkUsernameExist(username)
        const isValid = !exists || username === defaultValue
        if (currentRequestID.current !== requestID) {
          return
        }

        if (isValid) {
          e.target.setCustomValidity('')
          onChange({ target: { value: username, name } })
          setState(STATES.VALID)
        } else {
          e.target.setCustomValidity('Username already taken')
          e.target.checkValidity(e)
          setState(STATES.INVALID)
        }
      }, 750),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name]
  )

  const handleChange = useCallback(
    async e => {
      const _username = e.target.value.replace(/[^a-z0-9_]+$/gi, '')
      if (_username === username) {
        return
      }

      setState(STATES.VALIDATING)
      setUsername(_username)
      resetInput()

      const requestID = new Date().getTime()
      currentRequestID.current = requestID

      if (_username.length < MIN_USERNAME_LENGTH) {
        e.target.setCustomValidity(`Please enter a minimum of ${MIN_USERNAME_LENGTH} characters`)
        e.target.checkValidity(e)
        setState(STATES.INVALID)

        return
      }

      e.persist()
      validateUsername(e, _username, requestID)
    },
    [resetInput, validateUsername, username]
  )

  return (
    <div className="relative">
      <Input
        {...rest}
        name={uuidv4}
        value={valueOrEmptyString(username)}
        onChange={handleChange}
        classNames={{ input: 'pl-8' }}
      >
        <div className="absolute top-1/2 transform -translate-y-1/2 left-5 text-sm text-white pb-1">
          @
        </div>
      </Input>
      <div className="absolute top-1/2 transform -translate-y-1/2 right-5 flex items-center">
        {is(state, STATES.VALID) && <AnimatedCheckIcon className="w-4 h-4 text-green-500" />}
        {is(state, STATES.VALIDATING) && <Spinner className="animate-spin text-white" />}
      </div>
    </div>
  )
}

UsernameInput.propTypes = {
  Input: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  username: PropTypes.string,
}

UsernameInput.defaultProps = {
  username: '',
}

export default UsernameInput
